import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import {
  AcademicCapIcon,
  ClockIcon,
  CalendarDaysIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { MOCK_DATA } from '../../../lib/mock/viag/learn/data/mockData';

// Utility function for background colors (same as in LearnDashboard)
const getBackgroundColor = (id) => {
  const colorMap = {
    'AGS001': 'bg-blue-500',
    'AGS002': 'bg-red-500',
    'AGS003': 'bg-indigo-500',
    'AGS004': 'bg-orange-500',
    'AGS005': 'bg-yellow-500',
    'AGS006': 'bg-purple-500',
    'AGS007': 'bg-green-500',
    'AGS008': 'bg-gray-500',
    'AGS009': 'bg-teal-500',
    'AGS010': 'bg-pink-500',
  };
  
  return colorMap[id] || 'bg-gray-400';
};

const InProgressCoursesPage = () => {
  const { t, locale } = useLanguage();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Simulate API call to get courses
    const fetchCourses = async () => {
      setTimeout(() => {
        // Filter only in_progress courses
        const inProgressCourses = MOCK_DATA.courses.filter(
          course => course.status === 'in_progress'
        );
        setCourses(inProgressCourses);
        setLoading(false);
      }, 800);
    };

    fetchCourses();
  }, []);

  // Filter courses based on search term
  const filteredCourses = courses.filter(course => {
    const matchesSearch = 
      searchTerm === '' ||
      (locale === 'vi' ? 
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) :
        course.titleEn.toLowerCase().includes(searchTerm.toLowerCase())) ||
      course.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.instructor.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  // Sort courses by progress (highest first)
  const sortedCourses = [...filteredCourses].sort((a, b) => b.progress - a.progress);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('learn.courseStatus.inProgress')}
          </h1>
          <p className="text-gray-500 mt-1">
            {t('learn.dashboard.coursesInProgress')}: {courses.length}
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          {/* Search box */}
          <div className="relative w-full sm:w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder={t('learn.myCourses.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Progress summary */}
      <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4">
        <h2 className="text-sm font-semibold text-indigo-800 mb-2">{t('learn.dashboard.progressOverview')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {courses.length > 0 ? (
            <>
              <div>
                <div className="text-sm text-gray-500">{t('learn.dashboard.overallCompletion')}</div>
                <div className="text-2xl font-bold text-indigo-700">
                  {Math.round(courses.reduce((sum, course) => sum + course.progress, 0) / courses.length)}%
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">{t('learn.courseInfo.due')} {t('learn.myCourses.sortOptions.dueDate')}</div>
                <div className="text-lg font-medium text-indigo-700">
                  {courses.find(c => c.dueDate)?.dueDate || 'N/A'}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">{t('learn.dashboard.completionRate')}</div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div
                    className="bg-indigo-600 h-2 rounded-full"
                    style={{ width: `${Math.round(courses.reduce((sum, course) => sum + course.progress, 0) / courses.length)}%` }}
                  ></div>
                </div>
              </div>
            </>
          ) : (
            <div className="col-span-3 text-center py-2 text-gray-500">
              {t('learn.myCourses.noCourses')}
            </div>
          )}
        </div>
      </div>

      {/* Courses grid */}
      {sortedCourses.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedCourses.map(course => (
            <div key={course.id} className="bg-white rounded-lg shadow overflow-hidden flex flex-col border border-gray-100">
              <div className={`h-36 relative flex items-center justify-center ${getBackgroundColor(course.id)} text-white`}>
                <span className="text-lg font-bold">{locale === 'vi' ? course.title.split(' ').slice(0, 2).join(' ') : course.titleEn.split(' ').slice(0, 2).join(' ')}</span>
                {course.isRequired && (
                  <div className="absolute top-2 right-2 bg-red-100 text-red-800 text-xs font-medium px-2 py-0.5 rounded">
                    {t('learn.courseInfo.required')}
                  </div>
                )}
                <div className="absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-medium bg-white/20">
                  {course.id}
                </div>
              </div>
              <div className="p-4 flex-grow">
                <div className="flex justify-between items-start">
                  <h3 className="font-medium text-gray-900 mb-1 line-clamp-2 flex-1">
                    {locale === 'vi' ? course.title : course.titleEn}
                  </h3>
                </div>
                <p className="text-gray-500 text-sm mt-2 mb-3">
                  {course.instructor} • {course.duration}
                </p>
                
                <div className="mb-4">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-500">{t('learn.courseInfo.progress')}</span>
                    <span className="text-gray-900 font-medium">{course.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-indigo-600 h-2 rounded-full"
                      style={{ width: `${course.progress}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                  {course.dueDate && (
                    <div className="flex items-center">
                      <CalendarDaysIcon className="h-3.5 w-3.5 mr-1 text-gray-400" />
                      <span>{t('learn.courseInfo.due')}: {course.dueDate}</span>
                    </div>
                  )}
                  {course.certification && (
                    <div className="flex items-center">
                      <AcademicCapIcon className="h-3.5 w-3.5 mr-1 text-gray-400" />
                      <span>{t('learn.courseInfo.hasCertification')}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="px-4 py-3 bg-gray-50 border-t">
                <Link 
                  to={`/learn/course/${course.id}`}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {t('learn.courseActions.continueLearning')}
                </Link>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white p-6 rounded-lg shadow text-center">
          <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t('learn.myCourses.noCourses')}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t('learn.myCourses.noCoursesDescription')}
          </p>
        </div>
      )}
    </div>
  );
};

export default InProgressCoursesPage; 